import { newIdempotencyKey } from '@micarahr/api-client';
import { apiTransport } from './api-client';

/**
 * Payroll configuration, in the calculation engine's own terms.
 *
 * The prototype kept its tables as display rows — a label, peso amounts as
 * money objects, a tax "rate percent". The engine keeps brackets: a salary
 * range and, for each side, either a fixed amount or a share. Showing the
 * engine's shape is the point of this page: what an administrator reads here
 * is what a pay run deducts with, with nothing translated in between.
 *
 * Amounts and shares stay decimal strings end to end, as the server stores
 * them. A share is a fraction (0.02 is two per cent); the page converts it for
 * display and back, and nothing does arithmetic on a float in between.
 */

export interface PayrollBracket {
  from: string;
  /** Null for the open-ended top bracket. */
  to: string | null;
  employeeShare?: string;
  employerShare?: string;
  employeeFixed?: string;
  employerFixed?: string;
}

export type PayrollTableKind = 'sss' | 'philhealth' | 'pagibig' | 'withholding_tax' | 'other';

export interface PayrollTableVersion {
  id: string;
  kind: PayrollTableKind;
  name: string;
  version: number;
  effectiveFrom: string;
  /** Null for the version currently in force. */
  effectiveTo: string | null;
  brackets: PayrollBracket[];
  legallyValidated: boolean;
  notes: string | null;
}

export interface PayrollRateRuleRow {
  id: string;
  category: string;
  key: string;
  name: string;
  multiplier: string;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  legallyValidated: boolean;
}

export interface PayrollCalendarPolicyRow {
  id: string;
  dayRateBasis: string;
  annualFactor: string | null;
  restDayWeekdays: number[];
  regularHolidaysAreWorkingDays: boolean;
  specialDaysAreWorkingDays: boolean;
  version: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  legallyValidated: boolean;
  notes: string | null;
}

export interface PayrollConfigurationView {
  tables: PayrollTableVersion[];
  rateRules: PayrollRateRuleRow[];
  calendarPolicies: PayrollCalendarPolicyRow[];
}

const day = (value: string | null) => (value ? value.slice(0, 10) : null);

export const apiPayrollConfigService = {
  /** GET /payroll/configuration — every version of all three, newest first. */
  async configuration(): Promise<PayrollConfigurationView> {
    const data = await apiTransport().get<{
      contributionTables: (PayrollTableVersion & { effectiveTo: string | null })[];
      rateRules: PayrollRateRuleRow[];
      calendarPolicies: PayrollCalendarPolicyRow[];
    }>('/api/v1/payroll/configuration');

    return {
      tables: data.contributionTables.map((table) => ({
        ...table,
        effectiveFrom: day(table.effectiveFrom)!,
        effectiveTo: day(table.effectiveTo),
      })),
      rateRules: data.rateRules.map((rule) => ({
        ...rule,
        effectiveFrom: day(rule.effectiveFrom)!,
        effectiveTo: day(rule.effectiveTo),
      })),
      calendarPolicies: data.calendarPolicies.map((policy) => ({
        ...policy,
        annualFactor: policy.annualFactor === null ? null : String(policy.annualFactor),
        effectiveFrom: day(policy.effectiveFrom)!,
        effectiveTo: day(policy.effectiveTo),
      })),
    };
  },

  /**
   * POST /payroll/contribution-tables/:id/versions. The server validates the
   * brackets as a whole — gaps, overlaps, a share above one — with the same
   * check the engine runs, and answers a bad table with every problem named.
   */
  async publishVersion(
    tableId: string,
    input: { effectiveFrom: string; brackets: PayrollBracket[]; notes?: string },
  ): Promise<PayrollTableVersion> {
    const created = await apiTransport().post<PayrollTableVersion>(
      `/api/v1/payroll/contribution-tables/${tableId}/versions`,
      input,
      { idempotencyKey: newIdempotencyKey() },
    );
    return { ...created, effectiveFrom: day(created.effectiveFrom)!, effectiveTo: null };
  },
};
